import { useState, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Archive, CalendarClock, FileClock, FileQuestion, FileText, Search, X } from 'lucide-react'
import {
  Callout, DateValue, EmptyState, ErrorState, KpiGrid, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useRetentionReview } from '../api'
import type { RetentionAnswer } from '../types'

/**
 * What may now be reviewed for destruction.
 *
 * <p>The screen deletes nothing and is not capable of deleting anything — the list is the
 * product. Retention is a decision somebody takes with a document in front of them; a button
 * that destroyed a year of records because a rule said so would be the most dangerous control
 * in the system.
 */
export function RetentionReviewPage() {
  useBreadcrumb()
  const review = useRetentionReview()
  const [search, setSearch] = useState('')

  const all = review.data ?? []
  const needle = search.trim().toLowerCase()
  const visible = needle ? all.filter((row) => row.title.toLowerCase().includes(needle)) : all

  const due = visible.filter((row) => row.dueForReview)
  const rest = visible.filter((row) => !row.dueForReview)
  const noRule = all.filter((row) => !row.dueForReview && !row.keepUntil).length

  return (
    <div className="space-y-5">
      <PageHeader
        title="Retention review"
        subtitle="What is past its keep-until date, and what nothing says anything about."
      />

      <Callout tone="safe" title="Nothing here deletes anything">
        A retention rule says when a document <em>may</em> be reviewed, not that it should go:
        the decision is a person's, taken with the document in front of them.
      </Callout>

      {review.isPending && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            {[0, 1, 2].map((index) => <Skeleton key={index} className="h-[104px] rounded-[var(--radius-card)]" />)}
          </div>
          <Skeleton className="h-48 rounded-[var(--radius-card)]" />
        </div>
      )}
      {review.isError && (
        <Card><ErrorState error={review.error} onRetry={() => void review.refetch()} /></Card>
      )}

      {review.isSuccess && all.length === 0 && (
        <Card>
          <EmptyState
            icon={Archive}
            title="Nothing to review"
            description="Either the library is empty, or no document has a retention rule attached yet."
          />
        </Card>
      )}

      {review.isSuccess && all.length > 0 && (
        <>
          <KpiGrid
            items={[
              {
                label: 'Due for review',
                value: all.filter((row) => row.dueForReview).length,
                icon: FileClock,
                tint: all.some((row) => row.dueForReview) ? 'orange' : 'green',
              },
              {
                label: 'Kept, not yet due',
                value: all.filter((row) => !row.dueForReview && row.keepUntil).length,
                icon: CalendarClock,
                tint: 'blue',
              },
              {
                label: 'No rule attached',
                value: noRule,
                icon: FileQuestion,
                tint: noRule > 0 ? 'purple' : 'green',
              },
            ]}
          />

          <div className="relative w-full sm:w-80">
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]" aria-hidden="true" />
            <Input
              aria-label="Search documents"
              placeholder="Search by document title"
              className="ps-9 pe-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                className="absolute end-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-[var(--color-muted)] hover:text-[var(--color-heading)]"
                onClick={() => setSearch('')}
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            )}
          </div>

          {visible.length === 0 && (
            <Card>
              <EmptyState
                icon={Search}
                title="No document matches that search"
                action={<Button variant="outline" size="sm" onClick={() => setSearch('')}>Clear search</Button>}
              />
            </Card>
          )}

          {due.length > 0 && (
            <DocumentGroup
              title="Due for review"
              rows={due}
              icon={FileClock}
              accent="bg-[var(--color-tint-orange)] text-[var(--color-warning)]"
              badge={<StatusBadge status="DUE" tone="warning" label="Review due" />}
            />
          )}
          {rest.length > 0 && (
            <DocumentGroup
              title="Not yet, or nothing says"
              rows={rest}
              icon={FileText}
              accent="bg-[var(--color-page)] text-[var(--color-muted)]"
            />
          )}
        </>
      )}
    </div>
  )
}

function DocumentGroup({ title, rows, icon: Icon, accent, badge }: {
  title: string
  rows: RetentionAnswer[]
  icon: LucideIcon
  accent: string
  badge?: ReactNode
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-5 py-3.5">
        <h3 className="text-[15px] font-semibold text-[var(--color-heading)]">{title}</h3>
        <span className="rounded-full bg-[var(--color-page)] px-2.5 py-0.5 text-xs font-semibold tabular-nums text-[var(--color-body)]">
          {rows.length}
        </span>
      </div>
      <ul className="divide-y divide-[var(--color-border)]">
        {rows.map((row) => (
          <li
            key={row.documentId}
            className="flex items-center gap-3 px-5 py-3 text-sm transition-colors hover:bg-[color-mix(in_srgb,var(--color-primary)_4%,transparent)]"
          >
            <span className={`grid size-9 shrink-0 place-items-center rounded-lg ${accent}`}>
              <Icon className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-[var(--color-heading)]">{row.title}</p>
              <p className="text-xs text-[var(--color-muted)]">
                {row.keepUntil
                  ? <>Keep until <DateValue value={row.keepUntil} /></>
                  : (row.dueForReview ? 'No keep-until date' : (row.note ?? 'No retention rule is attached.'))}
                {row.dueForReview && row.note ? ` · ${row.note}` : ''}
              </p>
            </div>
            {badge}
          </li>
        ))}
      </ul>
    </Card>
  )
}
